import React from 'react';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import { styled } from '@uhg-abyss/web/tools/styled';
import { SharedDialog } from '../../../common/SharedDialog';
import { surveysApi } from './api/surveysApi';
import type { SurveyAdminRecord, SurveyWriteInput } from './types';
import validationConfig from '../../../validation-config.json';

const vc = validationConfig.SURVEY_EDITOR_DIALOG;

const INPUT_LABEL_COLOR = '$web.semantic.color.text.content.primary';

const textInputCss = {
  'abyss-text-input-root': {
    width: '100%',
    minWidth: '12rem',
  },
  'abyss-text-input-label': {
    color: INPUT_LABEL_COLOR,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  'abyss-text-input': {
    minHeight: '2.5rem',
    fontSize: '0.95rem',
  },
} as const;

interface SurveyEditorDialogProps {
  isOpen: boolean;
  survey: SurveyAdminRecord | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}

const FormGrid = styled('div', {
  display: 'grid',
  gap: '1rem',
});

const DateFieldWrapper = styled('div', {
  '& .abyss-date-input-format-text': {
    display: 'none',
  },
});

const FieldError = styled(Text, {
  color: '#c0392b',
  fontSize: '0.8rem',
  margin: 0,
});

interface FormState {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
}

interface FormErrors {
  name?: string;
  startDate?: string;
  endDate?: string;
}

function isEndDateBeforeStartDate(state: FormState): boolean {
  if (!state.startDate.trim() || !state.endDate.trim()) return false;
  const start = Date.parse(state.startDate);
  const end = Date.parse(state.endDate);
  if (isNaN(start) || isNaN(end)) return false;
  return end < start;
}

function isStartDateInPast(value: string): boolean {
  if (!value.trim()) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const parsed = new Date(value);
  return !isNaN(parsed.getTime()) && parsed < today;
}

function validate(state: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!state.name.trim()) errors.name = vc.name.errorMessage;
  if (!state.startDate.trim()) errors.startDate = vc.startDate.errorMessage;
  else if (isStartDateInPast(state.startDate)) errors.startDate = vc.startDate.mustNotBePast;
  if (!state.endDate.trim()) errors.endDate = vc.endDate.errorMessage;
  if (!errors.endDate && isEndDateBeforeStartDate(state)) {
    errors.endDate = vc.endDate.mustNotBeBeforeStart;
  }
  return errors;
}

function validateField(
  field: keyof FormState,
  state: FormState
): string | undefined {
  if (field === 'name' && !state.name.trim()) return vc.name.errorMessage;
  if (field === 'startDate') {
    if (!state.startDate.trim()) return vc.startDate.errorMessage;
    if (isStartDateInPast(state.startDate)) return vc.startDate.mustNotBePast;
  }
  if (field === 'endDate') {
    if (!state.endDate.trim()) return vc.endDate.errorMessage;
    if (isEndDateBeforeStartDate(state)) return vc.endDate.mustNotBeBeforeStart;
  }
  return undefined;
}

function hasErrors(errors: FormErrors): boolean {
  return Object.keys(errors).length > 0;
}

function formatDateForInput(value: string | null | undefined): string {
  if (!value) return '';
  // Handle "YYYY-MM-DD HH:mm:ss" or ISO strings
  return value.substring(0, 10);
}

export const SurveyEditorDialog = ({
  isOpen,
  survey,
  onClose,
  onSaved,
}: SurveyEditorDialogProps) => {
  const isEdit = survey !== null;
  const [form, setForm] = React.useState<FormState>({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
  });
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const [saving, setSaving] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [addAnother, setAddAnother] = React.useState(false);
  // Abyss DateInput fires onChange then onBlur within the same event batch, before
  // this component re-renders - so `form` in handleBlur's closure can be stale.
  // Mirror the latest value here so validation always sees the just-picked date.
  const formRef = React.useRef(form);
  formRef.current = form;

  React.useEffect(() => {
    if (isOpen) {
      if (survey) {
        setForm({
          name: survey.name ?? '',
          description: survey.description ?? '',
          startDate: formatDateForInput(survey.startDate),
          endDate: formatDateForInput(survey.endDate),
        });
      } else {
        setForm({ name: '', description: '', startDate: '', endDate: '' });
      }
      setErrors({});
      setTouched({});
      setSaving(false);
      setServerError(null);
      setAddAnother(false);
    }
  }, [isOpen, survey]);

  const handleChange = (field: keyof FormState) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const next = { ...formRef.current, [field]: value };
      formRef.current = next;
      setForm(next);
      // Do not mark touched on change - only on blur
      // This prevents premature error display while user is still typing/selecting
      if (field === 'startDate' || field === 'endDate') {
        // Live cross-check so the Save/Update disable reason is never hidden behind an untouched field
        setErrors((prev) => {
          return {
            ...prev,
            ...(field === 'startDate' ? { startDate: validateField('startDate', next) } : {}),
            endDate: validateField('endDate', next),
          };
        });
      }
    };
  };

  const handleBlur = (field: keyof FormState) => {
    return () => {
      // Blurring startDate can flip endDate's cross-field validity, so re-touch/re-validate both
      const alsoRevalidateEndDate = field === 'startDate';
      setTouched((prev) => {
        return alsoRevalidateEndDate
          ? { ...prev, startDate: true, endDate: true }
          : { ...prev, [field]: true };
      });
      const latestForm = formRef.current;
      const fieldError = validateField(field, latestForm);
      setErrors((prev) => {
        return {
          ...prev,
          [field]: fieldError,
          ...(alsoRevalidateEndDate ? { endDate: validateField('endDate', latestForm) } : {}),
        };
      });
    };
  };

  const handleSave = async (keepOpen: boolean) => {
    const allTouched = {
      name: true,
      description: true,
      startDate: true,
      endDate: true,
    };
    setTouched(allTouched);
    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (hasErrors(validationErrors)) return;

    setSaving(true);
    setServerError(null);

    const body: SurveyWriteInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      startDate: `${form.startDate.trim()} 00:00:00`,
      endDate: `${form.endDate.trim()} 23:59:00`,
    };

    try {
      if (isEdit && survey) {
        await surveysApi.updateSurvey(survey.surveyId, body);
      } else {
        await surveysApi.createSurvey(body);
      }

      if (keepOpen) {
        setForm({ name: '', description: '', startDate: '', endDate: '' });
        setTouched({});
        setErrors({});
        setAddAnother(true);
        onSaved();
      } else {
        onSaved();
        onClose();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred';
      setServerError(msg);
    } finally {
      setSaving(false);
    }
  };

  const showError = (field: keyof FormErrors) => {
    if (field === 'startDate' && errors.startDate === vc.startDate.mustNotBePast) {
      return true;
    }
    if (field === 'endDate' && errors.endDate === vc.endDate.mustNotBeBeforeStart) {
      return true;
    }
    return touched[field] && errors[field];
  };

  // Check if form is currently valid (all required fields have values)
  const isFormValid = () => {
    return (
      form.name.trim() !== '' &&
      form.startDate.trim() !== '' &&
      form.endDate.trim() !== '' &&
      !isNaN(Date.parse(form.startDate)) &&
      !isNaN(Date.parse(form.endDate)) &&
      !isStartDateInPast(form.startDate) &&
      !isEndDateBeforeStartDate(form)
    );
  };

  return (
    <SharedDialog
      mode="app"
      ariaLabel={isEdit ? 'Update RFI' : 'Add RFI'}
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Update RFI' : 'Add RFI'}
      size="md"
      primaryAction={{
        label: saving ? 'Saving…' : isEdit ? 'Update' : 'Save',
        disabled: saving || !isFormValid(),
        onClick: () => {
          return handleSave(false);
        },
      }}
      secondaryAction={{
        label: 'Cancel',
        variant: 'outline',
        disabled: saving,
        onClick: onClose,
      }}
      tertiaryAction={
        !isEdit
          ? {
              label: saving ? 'Saving…' : 'Save & Add Another',
              variant: 'outline',
              disabled: saving || !isFormValid(),
              onClick: () => {
                return handleSave(true);
              },
            }
          : undefined
      }
    >
      <FormGrid>
        {serverError && <FieldError>{serverError}</FieldError>}
        {addAnother && (
          <Text style={{ color: '#1a7a45', margin: 0, fontSize: '0.85rem' }}>
            RFI saved successfully. Add another below.
          </Text>
        )}

        <div>
          <TextInput
            id="survey-name"
            label="RFI Name"
            isRequired
            value={form.name}
            css={textInputCss}
            onChange={handleChange('name')}
            onBlur={handleBlur('name')}
            isDisabled={saving}
          />
          {showError('name') && <FieldError>{errors.name}</FieldError>}
        </div>

        <div>
          <TextInput
            id="survey-description"
            label="RFI Description"
            value={form.description}
            css={textInputCss}
            onChange={handleChange('description')}
            isDisabled={saving}
          />
        </div>

        <DateFieldWrapper>
          <DateInput
            id="survey-start-date"
            label="Start Date (YYYY-MM-DD)"
            isRequired
            dateFormat="YYYY-MM-DD"
            value={form.startDate}
            onChange={handleChange('startDate')}
            onBlur={handleBlur('startDate')}
            isDisabled={saving}
            errorMessage={showError('startDate') ? errors.startDate : undefined}
          />
        </DateFieldWrapper>

        <DateFieldWrapper>
          <DateInput
            id="survey-end-date"
            label="End Date (YYYY-MM-DD)"
            isRequired
            dateFormat="YYYY-MM-DD"
            value={form.endDate}
            onChange={handleChange('endDate')}
            onBlur={handleBlur('endDate')}
            isDisabled={saving}
            errorMessage={showError('endDate') ? errors.endDate : undefined}
          />
        </DateFieldWrapper>
      </FormGrid>
    </SharedDialog>
  );
};
